import { Tour, TourStep } from '@pncl/common-models';

type HTMLElementWithCheckVisibility = HTMLElement & {
  checkVisibility?: () => boolean;
};

const overflowProperties = ['overflow', 'overflow-x', 'overflow-y'] as const;

type OverflowProperty = typeof overflowProperties[number];

interface OverflowStyleSnapshot {
  property: OverflowProperty;
  priority: string;
  value: string;
}

interface OverflowSnapshot {
  element: HTMLElement;
  styles: OverflowStyleSnapshot[];
  scrollLeft: number;
  scrollTop: number;
}

interface HitTestPoint {
  x: number;
  y: number;
}

export function getDisplayedTourElements(selector: string): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(isElementDisplayed);
}

// checkVisibility can report true for zero-size elements, so require a layout box as well.
export function isElementDisplayed(element: HTMLElement): boolean {
  const elementWithCheckVisibility = element as HTMLElementWithCheckVisibility;
  if (elementWithCheckVisibility.checkVisibility?.() === false) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

// A tour cannot start unless every step target is visible and directly hit-testable.
export function hasRequiredTourSelectors(tour: Tour): boolean {
  return Array.from(new Set(tour.steps.map((step) => step.element))).every((selector) =>
    getDisplayedTourElements(selector).some(ownsVisibleCenter),
  );
}

// Driver's overlay owns hit testing during an active tour, so only require layout visibility here.
export function hasDisplayedStepSelectors(step: TourStep, hoverSelector?: string): boolean {
  return getStepSelectors(step, hoverSelector).every(
    (selector) => getDisplayedTourElements(selector).length > 0,
  );
}

// Include hidden matches so responsive variants are observed before they become displayed.
export function getStepElementsIncludingHidden(
  step: TourStep,
  hoverSelector?: string,
): HTMLElement[] {
  const elements = new Set<HTMLElement>();

  getStepSelectors(step, hoverSelector).forEach((selector) => {
    document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
      elements.add(element);
    });
  });

  return Array.from(elements);
}

// Keep the validated match while it remains displayed; Driver's overlay prevents
// hit testing on step entry, so only switch when a responsive variant replaces it.
export function resolveTourStepElement(
  selector: string,
  fallback?: HTMLElement,
): HTMLElement | undefined {
  const displayedElements = getDisplayedTourElements(selector);
  const hitTestableElement = displayedElements.find(ownsVisibleCenter);

  if (hitTestableElement) {
    return hitTestableElement;
  }

  if (!fallback) {
    return undefined;
  }

  if (displayedElements.includes(fallback)) {
    return fallback;
  }

  return displayedElements[0] ?? fallback;
}

// Hover tours stay open while the pointer remains on the trigger, the active target, or the popover.
export function isWithinHoverRegion(params: {
  hoverSelector: string;
  activeStep?: TourStep;
  activePopoverElement?: HTMLElement;
  target: EventTarget | null;
}): boolean {
  if (!(params.target instanceof Node)) {
    return false;
  }

  const target = params.target;

  return getHoverRegionElements(
    params.hoverSelector,
    params.activeStep,
    params.activePopoverElement,
  ).some((element) => element === target || element.contains(target));
}

// Hover-triggered tours should only start while the trigger element is actually hovered.
export function isHoverTriggerActive(hoverSelector: string): boolean {
  return getDisplayedTourElements(hoverSelector).some((element) => element.matches(':hover'));
}

// Driver.js calls target.scrollIntoView() after onHighlightStarted, before its
// active-element CSS has been applied. Override only that next call so we can
// keep Driver's flow but adjust the browser scroll behavior at the right time.
export function useNearestScrollIntoViewForNextCall(element: Element): void {
  const originalScrollIntoView = element.scrollIntoView.bind(element);
  const restore = () => delete (element as Partial<Pick<Element, 'scrollIntoView'>>).scrollIntoView;

  Object.defineProperty(element, 'scrollIntoView', {
    configurable: true,
    value: (options?: ScrollIntoViewOptions) => {
      restore();
      // Hidden-overflow ancestors can still be scrolled by scrollIntoView when
      // the target contains negative-position children. Use overflow: clip for
      // this single call so they keep clipping without becoming scroll targets.
      const restoreOverflow = clipHiddenOverflowAncestors(element);

      try {
        originalScrollIntoView({
          ...(options ?? {}),
          block: 'nearest',
          inline: 'nearest',
        });
      } finally {
        // Always restore immediately so the tour target and surrounding UI keep
        // their original inline styles and scroll positions after Driver moves.
        restoreOverflow();
      }
    },
  });

  // Keep this element-scoped override briefly in case Driver defers scrolling.
  // It restores itself on the first call; this timeout is only fallback cleanup.
  window.setTimeout(restore, 50);
}

function clipHiddenOverflowAncestors(element: Element): () => void {
  const snapshots: OverflowSnapshot[] = [];
  let current = element;

  // Walk from the target upward. Only ancestors with computed overflow:hidden
  // need protection; other ancestors should keep their normal scroll behavior.
  while (current instanceof HTMLElement) {
    const styles = getComputedStyle(current);
    const hasHiddenOverflow = [styles.overflow, styles.overflowX, styles.overflowY].includes(
      'hidden',
    );

    if (hasHiddenOverflow) {
      const snapshotElement = current;

      snapshots.push({
        element: snapshotElement,
        styles: overflowProperties.map((property) => ({
          property,
          priority: snapshotElement.style.getPropertyPriority(property),
          value: snapshotElement.style.getPropertyValue(property),
        })),
        scrollLeft: snapshotElement.scrollLeft,
        scrollTop: snapshotElement.scrollTop,
      });
      overflowProperties.forEach((property) =>
        snapshotElement.style.setProperty(property, 'clip', 'important'),
      );
    }

    current = current.parentElement as HTMLElement;
  }

  return () => {
    // Restore styles first, then put scroll offsets back in case the intercepted
    // scrollIntoView moved a hidden-overflow ancestor.
    snapshots.forEach(({ element: snapshotElement, styles }) => {
      styles.forEach(({ property, priority, value }) => {
        if (value) {
          snapshotElement.style.setProperty(property, value, priority);
        } else {
          snapshotElement.style.removeProperty(property);
        }
      });
    });

    snapshots.forEach(({ element: snapshotElement, scrollLeft, scrollTop }) => {
      snapshotElement.scrollLeft = scrollLeft;
      snapshotElement.scrollTop = scrollTop;
    });
  };
}

function ownsVisibleCenter(element: HTMLElement): boolean {
  const hitTestPoint = getHitTestPoint(element.getBoundingClientRect());
  if (!hitTestPoint) {
    return false;
  }

  // The hit test rejects selector matches covered by another responsive layout variant.
  // contains() is inclusive of the element itself and returns false for null.
  const topElement = document.elementFromPoint(hitTestPoint.x, hitTestPoint.y);
  return element.contains(topElement);
}

function getHitTestPoint(rect: DOMRect): HitTestPoint | undefined {
  // elementFromPoint cannot test offscreen coordinates, so center the visible intersection.
  const left = Math.max(0, rect.left);
  const right = Math.min(window.innerWidth, rect.right);
  const top = Math.max(0, rect.top);
  const bottom = Math.min(window.innerHeight, rect.bottom);

  if (left >= right || top >= bottom) {
    return undefined;
  }

  return {
    x: left + (right - left) / 2,
    y: top + (bottom - top) / 2,
  };
}

// Hover steps depend on both the step target itself and the original hover trigger element.
function getStepSelectors(step: TourStep, hoverSelector?: string): string[] {
  const selectors = new Set<string>([step.element]);

  if (hoverSelector) {
    selectors.add(hoverSelector);
  }

  return Array.from(selectors);
}

// Collect every element that should count as "still inside" the active hover interaction.
function getHoverRegionElements(
  hoverSelector: string,
  activeStep?: TourStep,
  activePopoverElement?: HTMLElement,
): HTMLElement[] {
  const selectors = new Set<string>([hoverSelector]);

  if (activeStep?.element) {
    selectors.add(activeStep.element);
  }

  const elements = Array.from(selectors).flatMap((selector) => getDisplayedTourElements(selector));
  return activePopoverElement ? [...elements, activePopoverElement] : elements;
}
